import {
  OBSTRUCTION_MEANS,
  OBSTRUCTION_WORDS,
  type Interference,
  type Obstruction,
} from '@hotlist/shared';

/**
 * What you can do to a camera you are standing next to.
 *
 * A menu rather than buttons in the action bar, because there are seven of
 * them — four ways to cover a lens, plus break, take and destroy — and the bar
 * is the row somebody is aiming at while driving. It should not triple in width
 * because a pole came within thirty meters.
 *
 * ## The gradient is the content
 *
 * §8's whole argument about interference is that the tiers are *different*, and
 * a menu is the first place that has been legible. Covering is done from a car
 * window and comes off in hours; taking one off a pole is a different act
 * entirely, needs you much closer, and brings it back somewhere else. Each row
 * says what it costs and roughly how long it lasts, so the choice is made with
 * the gradient in front of you rather than by pressing whichever button is
 * nearest your thumb.
 *
 * Rows out of reach are shown and disabled rather than hidden. "You are not
 * close enough to unbolt this" is the information — hiding it would leave a
 * player who never parks close enough believing the game has three tiers.
 */

export interface CameraMenuHandlers {
  cover: (means: Obstruction) => void;
  heavier: (tier: Interference) => void;
}

interface Row {
  kind: 'cover' | 'tier';
  means?: Obstruction;
  tier?: Interference;
  title: string;
  detail: string;
  /** Meters you have to be within, mirroring INTERFERENCE_REACH. */
  reach: number;
}

const ROWS: readonly Row[] = Object.freeze([
  ...OBSTRUCTION_MEANS.map(
    (means): Row => ({
      kind: 'cover',
      means,
      title: `Cover it with ${OBSTRUCTION_WORDS[means]}`,
      detail:
        means === 'balloons'
          ? 'Off in an hour or two. Somebody will see it from the road.'
          : means === 'bag'
            ? 'A few hours. Nothing to clean up afterwards.'
            : means === 'tape'
              ? 'Over the glass. Longer to notice and longer to sort out.'
              : 'Has to be cleaned off. The slowest way to cover one.',
      reach: 30,
    }),
  ),
  {
    kind: 'tier',
    tier: 'damage',
    title: 'Break it',
    detail: 'A day or two, and a service call rather than somebody with a ladder.',
    reach: 20,
  },
  {
    kind: 'tier',
    tier: 'take',
    title: 'Take it off the pole',
    detail: 'Days. It comes back somewhere else entirely, and not where it was.',
    reach: 12,
  },
]);

export class CameraMenu {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly title: HTMLElement;
  private readonly handlers: CameraMenuHandlers;
  private distance = Infinity;

  constructor(handlers: CameraMenuHandlers) {
    this.handlers = handlers;
    this.root = must('camera-menu');
    this.list = must('camera-menu-list');
    this.title = must('camera-menu-title');

    this.root.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.dataset.close !== undefined || target === this.root) {
        this.close();
        return;
      }
      const row = target.closest<HTMLElement>('.camera-row');
      if (!row || row.classList.contains('out')) return;
      const means = row.dataset.cover as Obstruction | undefined;
      const tier = row.dataset.tier as Interference | undefined;
      if (means) this.handlers.cover(means);
      else if (tier) this.handlers.heavier(tier);
      this.close();
    });
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  close(): void {
    this.root.hidden = true;
  }

  show(distance: number): void {
    this.distance = distance;
    // Feet. Every other distance the player is shown is in feet or miles, and
    // one panel quietly switching to meters is the sort of thing that makes
    // somebody misjudge whether they can reach a camera.
    this.title.textContent = `Camera, ${Math.round(distance / 0.3048)} ft away`;
    this.list.replaceChildren();

    for (const row of ROWS) {
      const within = this.distance <= row.reach;
      const element = document.createElement('div');
      element.className = within ? 'camera-row' : 'camera-row out';
      if (row.means) element.dataset.cover = row.means;
      if (row.tier) element.dataset.tier = row.tier;

      const title = document.createElement('div');
      title.className = 'camera-row-title';
      title.textContent = row.title;

      const detail = document.createElement('div');
      detail.className = 'camera-row-detail';
      // What is stopping you, rather than nothing at all. There is no walking
      // in this game, so being closer is the only way to say "this took time".
      detail.textContent = within
        ? row.detail
        : `Not from here. You have to be within ${row.reach} m.`;

      element.append(title, detail);
      this.list.append(element);
    }

    this.root.hidden = false;
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
